import type {
    AgGaugeColorStop,
    AgLinearGaugePreset,
    AgLinearGaugeTarget,
    AgLinearGaugeThemeableOptions,
    AgRadialGaugePreset,
    AgRadialGaugeTarget,
    AgRadialGaugeThemeableOptions,
    FillsOptions,
} from 'ag-charts-types';

import {
    type OptionsDefs,
    and,
    arrayLength,
    arrayOf,
    arrayOfDefs,
    boolean,
    callback,
    color,
    constant,
    greaterThan,
    lessThan,
    number,
    numericValue,
    optionsDefs,
    or,
    positiveNumber,
    ratio,
    required,
    string,
    undocumented,
    union,
} from '../state/validation';
import { without } from '../utils/data/object';
import {
    autoSizedLabelOptionsDefs,
    commonSeriesOptionsDefs,
    commonSeriesThemeableOptionsDefs,
    numberFormatValidator,
    seriesLabelOptionsDefs,
    tooltipOptionsDefs,
} from './chartDefaults';
import {
    colorOrRef,
    colorStopsOrderValidator,
    fillOptionsDef,
    fontOptionsDef,
    lineDashOptionsDef,
    strokeOptionsDef,
} from './optionsDefaults';

const fillsOptionsDef: OptionsDefs<FillsOptions> = {
    fills: and(
        arrayLength(2),
        arrayOf(optionsDefs<AgGaugeColorStop>({ color: colorOrRef, stop: numericValue }, '')),
        colorStopsOrderValidator
    ),
    fillMode: union('continuous', 'discrete'),
};

export const linearGaugeTargetOptionsDef: OptionsDefs<AgLinearGaugeTarget> = {
    value: required(numericValue),
    text: string,
    shape: or(
        union('circle', 'cross', 'diamond', 'heart', 'plus', 'pin', 'square', 'star', 'triangle', 'line'),
        callback
    ),
    placement: union('before', 'after', 'middle'),
    spacing: positiveNumber,
    size: positiveNumber,
    rotation: number,
    ...fillOptionsDef,
    ...strokeOptionsDef,
    ...lineDashOptionsDef,
};

export const radialGaugeTargetOptionsDef: OptionsDefs<AgRadialGaugeTarget> = {
    value: required(numericValue),
    text: string,
    shape: or(
        union('circle', 'cross', 'diamond', 'heart', 'plus', 'pin', 'square', 'star', 'triangle', 'line'),
        callback
    ),
    placement: union('inside', 'outside', 'middle'),
    spacing: positiveNumber,
    size: positiveNumber,
    rotation: number,
    label: {
        ...seriesLabelOptionsDefs,
        spacing: positiveNumber,
    },
    ...fillOptionsDef,
    ...strokeOptionsDef,
    ...lineDashOptionsDef,
};

export const linearGaugeSeriesThemeableOptionsDef: OptionsDefs<AgLinearGaugeThemeableOptions> = {
    ...without(commonSeriesThemeableOptionsDefs, ['listeners']),
    direction: union('horizontal', 'vertical'),
    cornerMode: union('container', 'item'),
    cornerRadius: positiveNumber,
    thickness: positiveNumber,
    scale: {
        min: and(numericValue, lessThan('max')),
        max: and(numericValue, greaterThan('min')),
        label: {
            enabled: boolean,
            formatter: callback,
            rotation: number,
            spacing: positiveNumber,
            minSpacing: positiveNumber,
            placement: union('before', 'after'),
            avoidCollisions: boolean,
            format: numberFormatValidator,
            ...fontOptionsDef,
        },
        interval: {
            values: arrayOf(numericValue),
            step: numericValue,
        },
        ...fillsOptionsDef,
        ...fillOptionsDef,
        ...strokeOptionsDef,
        ...lineDashOptionsDef,
    },
    segmentation: {
        enabled: boolean,
        spacing: positiveNumber,
        interval: {
            values: arrayOf(numericValue),
            step: numericValue,
            count: number,
        },
    },
    bar: {
        enabled: boolean,
        thickness: positiveNumber,
        thicknessRatio: ratio,
        ...fillsOptionsDef,
        ...fillOptionsDef,
        ...strokeOptionsDef,
        ...lineDashOptionsDef,
    },
    label: {
        ...autoSizedLabelOptionsDefs,
        text: string,
        spacing: positiveNumber,
        avoidCollisions: boolean,
        placement: union(
            'inside-start',
            'outside-start',
            'inside-end',
            'outside-end',
            'inside-center',
            'bar-inside',
            'bar-inside-end',
            'bar-outside-end',
            'bar-end'
        ),
    },
    tooltip: tooltipOptionsDefs,
};

export const linearGaugeSeriesOptionsDef: OptionsDefs<AgLinearGaugePreset> = {
    ...without(commonSeriesOptionsDefs, ['listeners']),
    ...linearGaugeSeriesThemeableOptionsDef,
    type: required(constant('linear-gauge')),
    value: required(numericValue),
    targets: arrayOfDefs(linearGaugeTargetOptionsDef, 'target options array'),
};

// @ts-expect-error undocumented option
linearGaugeSeriesOptionsDef.margin = undocumented(number);
// @ts-expect-error undocumented option
linearGaugeSeriesOptionsDef.defaultColorRange = undocumented(arrayOf(color));
// @ts-expect-error undocumented option
linearGaugeSeriesOptionsDef.defaultTarget = undocumented({
    ...linearGaugeTargetOptionsDef,
    value: number,
    label: {
        ...seriesLabelOptionsDefs,
        spacing: number,
    },
});

(linearGaugeSeriesOptionsDef as any).defaultScale = undocumented(linearGaugeSeriesOptionsDef.scale as any);
(linearGaugeSeriesOptionsDef.scale as any).defaultFill = undocumented(color);

export const radialGaugeSeriesThemeableOptionsDef: OptionsDefs<AgRadialGaugeThemeableOptions> = {
    ...without(commonSeriesThemeableOptionsDefs, ['listeners']),
    outerRadius: positiveNumber,
    innerRadius: positiveNumber,
    outerRadiusRatio: ratio,
    innerRadiusRatio: ratio,
    startAngle: number,
    endAngle: number,
    spacing: positiveNumber,
    cornerMode: union('container', 'item'),
    cornerRadius: positiveNumber,
    scale: {
        min: and(numericValue, lessThan('max')),
        max: and(numericValue, greaterThan('min')),
        label: {
            enabled: boolean,
            formatter: callback,
            rotation: number,
            spacing: positiveNumber,
            minSpacing: positiveNumber,
            avoidCollisions: boolean,
            format: numberFormatValidator,
            ...fontOptionsDef,
        },
        interval: {
            values: arrayOf(numericValue),
            step: numericValue,
        },
        ...fillsOptionsDef,
        ...fillOptionsDef,
        ...strokeOptionsDef,
        ...lineDashOptionsDef,
    },
    segmentation: {
        enabled: boolean,
        spacing: positiveNumber,
        interval: {
            values: arrayOf(numericValue),
            step: numericValue,
            count: number,
        },
    },
    bar: {
        enabled: boolean,
        ...fillsOptionsDef,
        ...fillOptionsDef,
        ...strokeOptionsDef,
        ...lineDashOptionsDef,
    },
    needle: {
        enabled: boolean,
        spacing: positiveNumber,
        radiusRatio: ratio,
        ...fillOptionsDef,
        ...strokeOptionsDef,
        ...lineDashOptionsDef,
    },
    label: {
        text: string,
        spacing: positiveNumber,
        ...autoSizedLabelOptionsDefs,
    },
    secondaryLabel: {
        text: string,
        ...autoSizedLabelOptionsDefs,
    },
    tooltip: tooltipOptionsDefs,
};

export const radialGaugeSeriesOptionsDef: OptionsDefs<AgRadialGaugePreset> = {
    ...without(commonSeriesOptionsDefs, ['listeners']),
    ...radialGaugeSeriesThemeableOptionsDef,
    type: required(constant('radial-gauge')),
    value: required(numericValue),
    targets: arrayOfDefs(radialGaugeTargetOptionsDef, 'target options array'),
};

// @ts-expect-error undocumented option
radialGaugeSeriesOptionsDef.defaultColorRange = undocumented(arrayOf(color));
// @ts-expect-error undocumented option
radialGaugeSeriesOptionsDef.defaultTarget = undocumented({
    ...radialGaugeTargetOptionsDef,
    value: number,
    label: {
        ...seriesLabelOptionsDefs,
        spacing: number,
    },
});
(radialGaugeSeriesOptionsDef.scale as any).defaultFill = undocumented(color);
